import React, { useEffect, useState } from "react";
import { X, ClipboardList } from "lucide-react";
import { api } from "../../lib/api";

const TYPES = [
  { value: "quiz", label: "Quiz", hint: "Assign a chapter quiz. Auto-marks complete when the student finishes it." },
  { value: "flashcards", label: "Flashcard review", hint: "Ask students to review a chapter's flashcards. Marked done on first view." },
  { value: "homework", label: "Homework", hint: "Free-text task. Students see it on their dashboard with an optional due date." },
];

export default function AssignmentFormModal({ groupId, chapters, onClose, onSaved }) {
  const [type, setType] = useState("quiz");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [chapterId, setChapterId] = useState(chapters[0]?.id || "");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const needsChapter = type === "quiz" || type === "flashcards";

  const handleSave = async () => {
    if (!title.trim()) {
      setErr("Please enter a title");
      return;
    }
    if (needsChapter && !chapterId) {
      setErr("Please pick a chapter");
      return;
    }
    setErr("");
    setSaving(true);
    try {
      await api.post(`/staff/groups/${groupId}/assignments`, {
        type,
        title: title.trim(),
        description: description.trim(),
        chapter_id: needsChapter ? chapterId : null,
        due_date: dueDate || null,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="assignment-form-modal">
      <div className="bg-bone w-full max-w-lg rounded-3xl border border-border overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center">
              <ClipboardList className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">New assignment</div>
              <div className="text-xs text-muted-foreground">Push a task to every student in this group</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-secondary text-muted-foreground" data-testid="assignment-form-close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>

        <div className="px-6 py-5 overflow-y-auto space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Type</label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  data-testid={`assignment-type-${t.value}`}
                  className={`px-3 py-2 rounded-xl border text-xs font-semibold uppercase tracking-wider transition ${
                    type === t.value
                      ? "bg-sage text-white border-sage"
                      : "bg-white border-border text-muted-foreground hover:border-sage/50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5">
              {TYPES.find((t) => t.value === type)?.hint}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Chapter 3 mock quiz"
              data-testid="assignment-form-title"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
              maxLength={200}
            />
          </div>

          {needsChapter && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Chapter</label>
              <select
                value={chapterId}
                onChange={(e) => setChapterId(e.target.value)}
                data-testid="assignment-form-chapter"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
              >
                {chapters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`Ch.${c.number} — ${c.title}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add instructions or context for students…"
              data-testid="assignment-form-description"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage min-h-[60px]"
              maxLength={600}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Due date (optional)</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="assignment-form-due"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
            />
          </div>

          {err && <div className="text-xs text-terracotta" data-testid="assignment-form-error">{err}</div>}
        </div>

        <footer className="px-6 py-4 border-t border-border bg-white flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-sage" data-testid="assignment-form-cancel">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="assignment-form-save"
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-60 text-white font-medium px-5 py-2 rounded-lg transition-all"
          >
            {saving ? "Creating…" : "Create assignment"}
          </button>
        </footer>
      </div>
    </div>
  );
}
